/**
 * EVO Cart Drawer helpers
 * - <evo-stepper>: +/- buttons around the theme's quantity-input; a change event is dispatched so
 *   the surrounding <line-item-quantity> updates the cart.
 * - Discount code removal: [data-discount-remove] clears the code(s) via cart/update.js and asks the
 *   theme to re-render the cart (same flow as applying a code in theme.js).
 * - <evo-review-summary>: store-wide Klaviyo rating (average + count over all products) for the
 *   trust row under the checkout button; fetched once per session, hidden until loaded.
 * - On-screen keyboard: keeps the discount-code field visible while typing on phones/tablets (see below).
 */
class EvoStepper extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[is="quantity-input"]');
    this.addEventListener("click", this._onClick.bind(this));
  }

  _onClick(event) {
    const button = event.target.closest("[data-step]");
    if (!button || !this.input) return;
    event.preventDefault();

    const step = button.getAttribute("data-step");
    if (step === "up") {
      this.input.increaseQuantity();
    } else {
      // reaching 0 removes the line (cart/change with quantity 0)
      const min = parseInt(this.input.getAttribute("data-min")) || 0;
      const next = this.input.quantity - 1;
      if (next < min) return;
      this.input.value = String(next);
    }
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

if (!window.customElements.get("evo-stepper")) {
  window.customElements.define("evo-stepper", EvoStepper);
}

(function () {
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  async function postDiscount(code) {
    const formData = new FormData();
    formData.append("discount", code);
    const response = await fetch(`${root()}cart/update.js`, { method: "POST", body: formData, credentials: "same-origin" });
    if (!response.ok) throw new Error(`cart/update.js failed: ${response.status}`);
    return response.json();
  }

  async function removeDiscount(codeToRemove) {
    const cart = await fetch(`${root()}cart.js`, { credentials: "same-origin" }).then((r) => r.json());
    const keep = (Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
      .map((entry) => String(entry.code || "").trim())
      .filter((code) => code && code.toLowerCase() !== String(codeToRemove).trim().toLowerCase());

    // Clearing removes every code; re-apply the ones that should stay.
    await postDiscount("");
    for (const code of keep) {
      await postDiscount(code);
    }
  }

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-discount-remove]");
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      await removeDiscount(button.getAttribute("data-discount-remove"));
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (error) {
      console.error("Discount remove failed:", error);
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  });
})();

/**
 * <evo-review-summary company-id="…" template="__RATING__ Sterne · __COUNT__ Bewertungen" locale="de" hidden>
 * Uses the public endpoint behind Klaviyo's "all reviews" widget (CORS-open). The summary is cached in
 * sessionStorage so the drawer (which re-renders its HTML on every cart change) shows it instantly.
 */
class EvoReviewSummary extends HTMLElement {
  static CACHE_KEY = "evo:klaviyo-review-summary";
  static CACHE_TTL = 60 * 60 * 1000;
  static pending = null;

  connectedCallback() {
    const companyId = this.getAttribute("company-id");
    if (!companyId) return;
    EvoReviewSummary.load(companyId).then((summary) => {
      if (summary) this._render(summary);
    });
  }

  static load(companyId) {
    const cached = EvoReviewSummary._readCache(companyId);
    if (cached) return Promise.resolve(cached);
    if (EvoReviewSummary.pending) return EvoReviewSummary.pending;

    const url =
      `https://fast.a.klaviyo.com/reviews/api/client_reviews/all/?company_id=${encodeURIComponent(companyId)}` +
      "&limit=1&offset=0&sort=3&type=reviews";

    EvoReviewSummary.pending = fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const rating = Number(data?.summary?.star_rating);
        const count = Number(data?.summary?.review_count);
        if (!rating || !count) return null;
        const summary = { rating, count, companyId, time: Date.now() };
        try {
          sessionStorage.setItem(EvoReviewSummary.CACHE_KEY, JSON.stringify(summary));
        } catch (e) {}
        return summary;
      })
      .catch(() => null)
      .finally(() => {
        EvoReviewSummary.pending = null;
      });

    return EvoReviewSummary.pending;
  }

  static _readCache(companyId) {
    try {
      const summary = JSON.parse(sessionStorage.getItem(EvoReviewSummary.CACHE_KEY));
      if (
        summary &&
        summary.companyId === companyId &&
        Date.now() - summary.time < EvoReviewSummary.CACHE_TTL
      ) {
        return summary;
      }
    } catch (e) {}
    return null;
  }

  _render({ rating, count }) {
    const locale = this.getAttribute("locale") || document.documentElement.lang || "de";
    const template = this.getAttribute("template") || "__RATING__ · __COUNT__";
    const target = this.querySelector("[data-review-summary-text]") || this;
    target.textContent = template
      .replaceAll("__RATING__", rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }))
      .replaceAll("__COUNT__", count.toLocaleString(locale));
    this.hidden = false;
  }
}

if (!window.customElements.get("evo-review-summary")) {
  window.customElements.define("evo-review-summary", EvoReviewSummary);
}

/**
 * On-screen keyboard vs. cart drawer (iOS Safari, Android Chrome).
 * The drawer is a full-height fixed panel with a tall dark checkout footer. When the keyboard opens for the
 * discount-code field only about half of the screen stays visible: browsers that shrink the viewport push the
 * footer up over the field, and iOS Safari pans the page, which drags the fixed drawer (and the field) out of view.
 * While a text field inside the open drawer has focus on a small or touch screen:
 *  - [data-evo-keyboard] is set on the drawer; for fields in the scroll area the CSS folds the footer away,
 *  - the drawer is pinned to the visual viewport (the area above the keyboard) via --evo-cart-vv-top/-height,
 *  - the field (with its apply button) is scrolled into the drawer's scroll area once the keyboard is up.
 * Blur, keyboard close, closing or re-rendering the drawer restores the normal layout.
 */
(function () {
  const SMALL = window.matchMedia("screen and (max-width: 699px)");
  const TOUCH_OR_SMALL = window.matchMedia("screen and (max-width: 699px), (pointer: coarse)");
  const FIELD =
    'input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], ' +
    'input[type="number"], input[type="url"], input[type="password"], textarea';
  const KEYBOARD_MIN = 120; // visible height must shrink by this much to count as "keyboard open"
  const vv = window.visualViewport;

  let drawer = null;
  let baseHeight = 0;
  let keyboardSeen = false;
  let frame = 0;
  let releaseTimer = 0;
  let revealTimers = [];
  let observer = null;

  const visibleHeight = () => (vv ? vv.height : window.innerHeight);
  const keyboardOpen = () => visibleHeight() < baseHeight - KEYBOARD_MIN;

  function focusedField() {
    const cartDrawer = document.getElementById("cart-drawer");
    const el = document.activeElement;
    if (!cartDrawer || !cartDrawer.open || !TOUCH_OR_SMALL.matches) return null;
    return el && el !== cartDrawer && cartDrawer.contains(el) && el.matches(FIELD) ? el : null;
  }

  function collapse(field) {
    drawer.setAttribute("data-evo-keyboard", field.closest('[slot="footer"]') ? "footer" : "body");
  }

  function start(field) {
    clearTimeout(releaseTimer);
    const cartDrawer = document.getElementById("cart-drawer");
    if (drawer !== cartDrawer) {
      stop();
      drawer = cartDrawer;
      baseHeight = Math.max(visibleHeight(), document.documentElement.clientHeight);
      keyboardSeen = false;
      listen(true);
    }
    // Phones: fold the footer right away, so it never slides up over the field while the keyboard animates in.
    // Larger touch screens (tablets, maybe with a hardware keyboard): only once an on-screen keyboard is up.
    if (SMALL.matches && (!keyboardSeen || keyboardOpen())) collapse(field);
    update();
    scheduleReveal();
  }

  function stop() {
    clearTimeout(releaseTimer);
    cancelAnimationFrame(frame);
    frame = 0;
    revealTimers.forEach(clearTimeout);
    revealTimers = [];
    if (!drawer) return;
    listen(false);
    drawer.removeAttribute("data-evo-keyboard");
    drawer.style.removeProperty("--evo-cart-vv-top");
    drawer.style.removeProperty("--evo-cart-vv-height");
    drawer = null;
  }

  function update() {
    frame = 0;
    if (!drawer) return;
    const field = focusedField();
    if (!field || document.getElementById("cart-drawer") !== drawer) {
      stop();
      return;
    }

    if (vv) {
      drawer.style.setProperty("--evo-cart-vv-top", `${Math.max(0, Math.round(vv.offsetTop))}px`);
      drawer.style.setProperty("--evo-cart-vv-height", `${Math.round(vv.height)}px`);
    }

    if (keyboardOpen()) {
      collapse(field);
      keyboardSeen = true;
    } else if (keyboardSeen) {
      // Keyboard dismissed while the field keeps focus (Android back button): give the footer back.
      drawer.removeAttribute("data-evo-keyboard");
    }
  }

  function requestUpdate() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function onViewportResize() {
    requestUpdate();
    scheduleReveal();
  }

  function scheduleReveal() {
    revealTimers.forEach(clearTimeout);
    // the keyboard animates for ~250–500 ms; re-check once it has settled
    revealTimers = [60, 350, 700].map((delay) => setTimeout(reveal, delay));
  }

  function reveal() {
    const field = focusedField();
    if (!drawer || !field || !drawer.hasAttribute("data-evo-keyboard")) return;
    const body = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="body"]');
    if (!body || field.closest('[slot="footer"]')) return;

    const target = field.closest("[data-discount-form]") || field;
    const area = body.getBoundingClientRect();
    const top = Math.max(area.top, vv ? vv.offsetTop : 0);
    const bottom = Math.min(area.bottom, vv ? vv.offsetTop + vv.height : window.innerHeight);
    const rect = target.getBoundingClientRect();
    const margin = 12;
    if (bottom <= top || (rect.top >= top + margin && rect.bottom <= bottom - margin)) return;

    const delta =
      rect.height > bottom - top - 2 * margin
        ? rect.top - top - margin
        : rect.top + rect.height / 2 - (top + bottom) / 2;
    body.scrollTop += delta;
  }

  function listen(on) {
    const method = on ? "addEventListener" : "removeEventListener";
    if (vv) {
      vv[method]("resize", onViewportResize);
      vv[method]("scroll", requestUpdate);
    }
    window[method]("resize", onViewportResize);
    drawer[method]("dialog:after-hide", stop);

    if (on) {
      // The drawer re-renders its HTML after a code is applied; the focused field disappears without a blur.
      observer = new MutationObserver(requestUpdate);
      observer.observe(drawer, { childList: true, subtree: true });
    } else if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  document.addEventListener("focusin", () => {
    const field = focusedField();
    if (field) start(field);
  });

  document.addEventListener("focusout", () => {
    if (!drawer) return;
    clearTimeout(releaseTimer);
    // Deferred: tapping "Anwenden" blurs the field first; the layout must not shift before that click lands.
    releaseTimer = setTimeout(() => {
      if (focusedField()) update();
      else stop();
    }, 150);
  });
})();

/*
 * Applied discount codes stay in sync with the cart: on "cart:change" (add to cart, stepper, remove) the theme only
 * swaps the line items and the footer, so the code row in the drawer header ("EVO -€3,69") kept the old amount.
 * Replace the whole .evo-cart__discount block from the freshly rendered drawer section instead.
 */
(() => {
  if (window.evoCartCodesSync) return;
  window.evoCartCodesSync = true;

  document.addEventListener("cart:change", (event) => {
    const sections = event.detail?.cart?.sections;
    if (!sections) return;
    const current = document.querySelector("#cart-drawer .evo-cart__codes, cart-drawer .evo-cart__codes")?.closest(".evo-cart__discount")
      || document.querySelector("cart-drawer .evo-cart__discount");
    if (!current) return;

    for (const html of Object.values(sections)) {
      if (typeof html !== "string" || !html.includes("evo-cart__discount")) continue;
      const updated = new DOMParser().parseFromString(html, "text/html").querySelector(".evo-cart__discount");
      if (!updated) continue;
      const currentCodes = current.querySelector(".evo-cart__codes");
      const updatedCodes = updated.querySelector(".evo-cart__codes");
      if (currentCodes && updatedCodes) currentCodes.replaceWith(updatedCodes);
      else if (currentCodes && !updatedCodes) currentCodes.remove();
      else if (!currentCodes && updatedCodes) current.appendChild(updatedCodes);
      break;
    }
  });
})();

/*
 * Empty cart: a discount code can already be entered. Liquid only lists codes that apply to items
 * (cart.discount_applications), so <evo-saved-codes> lists the codes stored on the cart from cart.js –
 * same pill + remove button as the applied codes; they are deducted once products are in the cart.
 */
if (!window.customElements.get("evo-saved-codes")) {
  window.customElements.define(
    "evo-saved-codes",
    class extends HTMLElement {
      async connectedCallback() {
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          const cart = await fetch(`${root}cart.js`, { credentials: "same-origin" }).then((r) => r.json());
          const codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
            .map((entry) => String(entry.code || "").trim())
            .filter(Boolean);
          if (!codes.length || !this.isConnected) return;

          const escape = (value) => value.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
          this.innerHTML = codes
            .map(
              (code) => `<div class="evo-cart__code" role="listitem">
              <span class="evo-cart__code-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></svg></span>
              <span class="evo-cart__code-name">${escape(code)}</span>
              <button type="button" class="evo-cart__code-remove" data-discount-remove="${escape(code)}" aria-label="Rabattcode ${escape(code)} entfernen"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button>
            </div>`
            )
            .join("");
          this.hidden = false;
        } catch (error) {
          /* no saved codes shown – the field still works */
        }
      }
    }
  );
}
